/**
 * Content Block Utilities
 * 
 * This module provides utility functions for managing structured content blocks
 * in day entries, enabling precise programmatic access while maintaining
 * human-readable display capabilities.
 * 
 * Part of Phase 2B: Bidirectional Linking Architecture
 */

/**
 * Validates a content block structure according to schema requirements
 * @param {Object} contentBlock - The content block to validate
 * @returns {Object} Validation result with isValid boolean and errors array
 */
export function validateContentBlock(contentBlock) {
  console.log('🔍 [ContentBlock] validateContentBlock: Starting validation', {
    hasContentBlock: !!contentBlock,
    contentBlockType: typeof contentBlock,
    contentBlockId: contentBlock?.id,
    contentBlockType_field: contentBlock?.type
  });

  const errors = [];

  // Required fields validation
  if (!contentBlock || typeof contentBlock !== 'object') {
    console.warn('❌ [ContentBlock] validateContentBlock: Invalid content block object', {
      contentBlock,
      type: typeof contentBlock
    });
    errors.push('Content block must be an object');
    return { isValid: false, errors };
  }

  if (!contentBlock.type) {
    errors.push('Content block type is required');
  }

  if (!contentBlock.id) {
    errors.push('Content block ID is required');
  }

  // Valid types check
  const validTypes = ['text', 'accommodation_reference', 'travel_reference', 'activity_reference', 'place_reference', 'day_reference', 'document_reference'];
  if (contentBlock.type && !validTypes.includes(contentBlock.type)) {
    errors.push(`Invalid content block type: ${contentBlock.type}. Must be one of: ${validTypes.join(', ')}`);
  }

  // ID format validation
  if (contentBlock.id && !/^[a-zA-Z0-9_]+$/.test(contentBlock.id)) {
    errors.push('Content block ID must contain only alphanumeric characters and underscores');
  }

  if (contentBlock.id && contentBlock.id.length < 5) {
    errors.push('Content block ID must be at least 5 characters long');
  }

  // Type-specific validation
  if (contentBlock.type === 'accommodation_reference') {
    if (!contentBlock.accommodationId) {
      errors.push('Accommodation ID is required for accommodation references');
    }

    if (!contentBlock.displayText) {
      errors.push('Display text is required for accommodation references');
    }

    if (!contentBlock.context) {
      errors.push('Context is required for accommodation references');
    }

    const validContexts = ['check-in', 'staying', 'check-out', 'single-day'];
    if (contentBlock.context && !validContexts.includes(contentBlock.context)) {
      errors.push(`Invalid context: ${contentBlock.context}. Must be one of: ${validContexts.join(', ')}`);
    }
  }

  if (contentBlock.type === 'text') {
    if (!contentBlock.text || typeof contentBlock.text !== 'string' || contentBlock.text.trim() === '') {
      errors.push('Text content cannot be empty');
    }
  }

  const result = {
    isValid: errors.length === 0,
    errors
  };

  console.log('✅ [ContentBlock] validateContentBlock: Validation complete', {
    contentBlockId: contentBlock?.id,
    contentBlockType: contentBlock?.type,
    isValid: result.isValid,
    errorCount: errors.length,
    errors: errors
  });

  return result;
}

/**
 * Generates a unique content block ID with optional type prefix
 * @param {string} type - The content block type (default: 'block')
 * @returns {string} Unique content block ID
 */
export function generateContentBlockId(type = 'block') {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 9);
  return `${type}_${timestamp}_${random}`;
}

/**
 * Sanitizes content block data to prevent XSS and ensure data integrity
 * @param {Object} contentBlock - The content block to sanitize
 * @returns {Object} Sanitized content block
 */
export function sanitizeContentBlock(contentBlock) {
  if (!contentBlock || typeof contentBlock !== 'object') {
    return contentBlock;
  }

  const sanitized = { ...contentBlock };

  // Sanitize text content
  if (sanitized.text && typeof sanitized.text === 'string') {
    // Remove potentially dangerous HTML tags but preserve basic formatting
    sanitized.text = sanitized.text
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '')
      .trim();
  }

  // Sanitize display text
  if (sanitized.displayText && typeof sanitized.displayText === 'string') {
    sanitized.displayText = sanitized.displayText
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '')
      .trim();
  }

  // Ensure required fields are strings
  if (sanitized.accommodationName) {
    sanitized.accommodationName = String(sanitized.accommodationName).trim();
  }

  if (sanitized.placeName) {
    sanitized.placeName = String(sanitized.placeName).trim();
  }

  // Ensure legacy accommodation references have a context
  if (sanitized.type === 'accommodation_reference' && !sanitized.context) {
    sanitized.context = 'staying';
  }

  return sanitized;
}

/**
 * Creates a new content block with proper structure and validation
 * @param {string} type - Content block type
 * @param {Object} data - Content block data
 * @returns {Object} Structured content block with timestamps
 */
export function createContentBlock(type, data) {
  console.log('🏗️ [ContentBlock] createContentBlock: Starting creation', {
    type,
    dataKeys: Object.keys(data || {}),
    hasId: !!data?.id,
    dataId: data?.id
  });

  const now = new Date().toISOString();

  const contentBlock = {
    type,
    id: data.id || generateContentBlockId(type),
    createdAt: now,
    updatedAt: now,
    ...data
  };

  console.log('🔧 [ContentBlock] createContentBlock: Content block assembled', {
    id: contentBlock.id,
    type: contentBlock.type,
    hasText: !!contentBlock.text,
    hasAccommodationId: !!contentBlock.accommodationId,
    hasDisplayText: !!contentBlock.displayText
  });

  // Sanitize the content block
  const sanitized = sanitizeContentBlock(contentBlock);
  console.log('🧹 [ContentBlock] createContentBlock: Content block sanitized');

  // Validate the content block
  const validation = validateContentBlock(sanitized);
  if (!validation.isValid) {
    console.error('❌ [ContentBlock] createContentBlock: Validation failed', {
      id: sanitized.id,
      type: sanitized.type,
      errors: validation.errors
    });
    throw new Error(`Invalid content block: ${validation.errors.join(', ')}`);
  }

  console.log('✅ [ContentBlock] createContentBlock: Content block created successfully', {
    id: sanitized.id,
    type: sanitized.type,
    createdAt: sanitized.createdAt
  });

  return sanitized;
}

/**
 * Updates an existing content block with new data
 * @param {Object} existingBlock - Existing content block
 * @param {Object} updates - Updates to apply
 * @returns {Object} Updated content block
 */
export function updateContentBlock(existingBlock, updates) {
  if (!existingBlock || typeof existingBlock !== 'object') {
    throw new Error('Existing content block is required');
  }

  const updatedBlock = {
    ...existingBlock,
    ...updates,
    id: existingBlock.id, // Preserve original ID
    createdAt: existingBlock.createdAt, // Preserve creation timestamp
    updatedAt: new Date().toISOString()
  };

  // Sanitize the updated content block
  const sanitized = sanitizeContentBlock(updatedBlock);

  // Validate the updated content block
  const validation = validateContentBlock(sanitized);
  if (!validation.isValid) {
    throw new Error(`Invalid content block update: ${validation.errors.join(', ')}`);
  }

  return sanitized;
}

/**
 * Checks if content is in structured format (array) or legacy format (string)
 * @param {*} content - Content to check
 * @returns {boolean} True if content is structured format
 */
export function isStructuredContent(content) {
  return Array.isArray(content);
}


/**
 * Finds a content block by ID within an array of content blocks
 * @param {Array} contentBlocks - Array of content blocks
 * @param {string} blockId - ID of the block to find
 * @returns {Object|null} Found content block or null
 */
export function findContentBlockById(contentBlocks, blockId) {
  if (!Array.isArray(contentBlocks) || !blockId) {
    return null;
  }

  return contentBlocks.find(block => block.id === blockId) || null;
}

/**
 * Removes a content block by ID from an array of content blocks
 * @param {Array} contentBlocks - Array of content blocks
 * @param {string} blockId - ID of the block to remove
 * @returns {Array} New array with the block removed
 */
export function removeContentBlockById(contentBlocks, blockId) {
  if (!Array.isArray(contentBlocks) || !blockId) {
    return contentBlocks || [];
  }

  return contentBlocks.filter(block => block.id !== blockId);
}

/**
 * Gets content blocks of a specific type
 * @param {Array} contentBlocks - Array of content blocks
 * @param {string} type - Type of blocks to retrieve
 * @returns {Array} Array of content blocks of the specified type
 */
export function getContentBlocksByType(contentBlocks, type) {
  if (!Array.isArray(contentBlocks) || !type) {
    return [];
  }

  return contentBlocks.filter(block => block.type === type);
}

/**
 * Consolidates content blocks according to the standard format:
 * - Single text block containing all user text (first)
 * - Accommodation reference blocks (last)
 * @param {Array} contentBlocks - Array of content blocks to consolidate
 * @returns {Array} Consolidated array of content blocks
 */
export function consolidateContentBlocks(contentBlocks) {
  console.log('🔧 [ContentBlock] consolidateContentBlocks: Starting consolidation', {
    inputBlockCount: contentBlocks?.length || 0,
    blockTypes: contentBlocks?.map(block => block.type) || []
  });

  if (!Array.isArray(contentBlocks)) {
    console.log('📝 [ContentBlock] consolidateContentBlocks: No blocks to consolidate');
    return [];
  }

  const result = [];

  // Extract and consolidate all text blocks into single text block
  const textBlocks = getContentBlocksByType(contentBlocks, 'text');
  if (textBlocks.length > 0) {
    const combinedText = textBlocks
      .map(block => block.text)
      .filter(text => text && text.trim())
      .join('\n')
      .trim();

    if (combinedText) {
      result.push(createContentBlock('text', {
        text: combinedText
      }));
      console.log('📝 [ContentBlock] consolidateContentBlocks: Created consolidated text block', {
        originalTextBlocks: textBlocks.length,
        consolidatedTextLength: combinedText.length
      });
    }
  }

  // Add accommodation reference blocks (preserve all metadata)
  const accommodationBlocks = getContentBlocksByType(contentBlocks, 'accommodation_reference');
  result.push(...accommodationBlocks);

  // Add other types of blocks (travel, activity, etc.)
  const otherBlocks = contentBlocks.filter(block =>
    block.type !== 'text' && block.type !== 'accommodation_reference'
  );
  result.push(...otherBlocks);

  console.log('✅ [ContentBlock] consolidateContentBlocks: Consolidation complete', {
    inputBlockCount: contentBlocks.length,
    outputBlockCount: result.length,
    textBlocks: result.filter(b => b.type === 'text').length,
    accommodationBlocks: result.filter(b => b.type === 'accommodation_reference').length,
    otherBlocks: result.filter(b => b.type !== 'text' && b.type !== 'accommodation_reference').length
  });

  return result;
} 